'use client';
import { useState, useCallback, useMemo } from 'react';
import { useAttachedRef, useWindowEvent, useDidUpdate } from '@s7/hooks';
import { unit } from '@s7/styles';
import { clamp, isNumber } from '@s7/utils';
import { safeWindow } from '../SwipeableDrawer.contract.js';
import { createDrawerSwipeGesture } from './drawerSwipeGesture.js';

function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
const COMMON_TRANSITION_PROPERTIES = {
    transitionDuration: '0.3s',
    transitionTimingFunction: 'ease-out'
};
const DRAGGING_TRANSITION = {
    transitionProperty: 'none'
};
const RELEASED_TRANSITION = _extends({
    transitionProperty: 'transform'
}, COMMON_TRANSITION_PROPERTIES);
const RELEASED_BACKDROP_TRANSITION = _extends({
    transitionProperty: 'opacity, background-color'
}, COMMON_TRANSITION_PROPERTIES);
const getOpacity = (offset)=>clamp(1 - offset / safeWindow.innerHeight, [
        0.2,
        0.75
    ]);
const convertSnapPointToPixels = (value)=>{
    if (isNumber(value)) {
        return value;
    }
    if (value.endsWith('%')) {
        return safeWindow.innerHeight * Number(value.slice(0, -1)) / 100;
    }
    if (value.endsWith('vh')) {
        return safeWindow.innerHeight * Number(value.slice(0, -2)) / 100;
    }
    return unit(value);
};
const useDrawerSwipeGesture = (snapPoint, opened, disabled, getAutoHeight, onClose)=>{
    const [dragging, setDragging] = useState(false);
    const [offset, setOffset] = useState(safeWindow.innerHeight);
    const [overlayOpacity, setOverlayOpacity] = useState(getOpacity(safeWindow.innerHeight));
    const updateSnapPoint = useCallback((point)=>{
        const calculatedPoint = point === 'auto' ? getAutoHeight() : point;
        const pixelOffset = convertSnapPointToPixels(calculatedPoint);
        setOffset(pixelOffset);
        setOverlayOpacity(getOpacity(pixelOffset));
    }, [
        getAutoHeight
    ]);
    const handleGestureEnd = (mode, onClose)=>{
        setDragging(false);
        if (mode === 'close') {
            updateSnapPoint(safeWindow.innerHeight);
            setTimeout(onClose, 300);
        } else {
            updateSnapPoint(snapPoint);
        }
    };
    const attachGesture = useCallback((element)=>{
        if (disabled) {
            return;
        }
        return createDrawerSwipeGesture({
            element,
            onStart: ()=>setDragging(true),
            onMove: (height)=>updateSnapPoint(height),
            onChange: (mode)=>handleGestureEnd(mode, onClose)
        });
    }, [
        updateSnapPoint,
        onClose,
        disabled
    ]);
    const [initializeDragHandleRef, dragHandleRef] = useAttachedRef(opened, (node)=>{
        var _attachGesture;
        (_attachGesture = attachGesture(node)) == null ? void 0 : _attachGesture.enable();
        updateSnapPoint(snapPoint);
    });
    const transitionProps = useMemo(()=>({
            onEntered: ()=>{
                var _attachGesture;
                updateSnapPoint(snapPoint);
                dragHandleRef.current && ((_attachGesture = attachGesture(dragHandleRef.current)) == null ? void 0 : _attachGesture.enable());
            },
            onExited: ()=>setOffset(safeWindow.innerHeight)
        }), [
        snapPoint,
        attachGesture
    ]);
    const getOverlayStyle = (style)=>dragging ? DRAGGING_TRANSITION : _extends({}, style, RELEASED_BACKDROP_TRANSITION);
    const contentStyle = _extends({}, offset > 0 && {
        transform: `translateY(${offset}px)`
    }, dragging ? DRAGGING_TRANSITION : RELEASED_TRANSITION);
    useWindowEvent('resize', ()=>updateSnapPoint(snapPoint), {
        passive: true
    });
    useDidUpdate(()=>updateSnapPoint(snapPoint), [
        snapPoint,
        dragHandleRef.current
    ]);
    return [
        initializeDragHandleRef,
        dragging,
        [
            contentStyle,
            transitionProps
        ],
        [
            overlayOpacity,
            getOverlayStyle
        ]
    ];
};

export { useDrawerSwipeGesture };
//# sourceMappingURL=useDrawerSwipeGesture.js.map
